import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Building2, Image, DollarSign, Star, FileImage,
  LogOut, Phone, Mail, MapPin, Settings, Calendar, Loader2
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { admin, token, logout } = useAuth();
  const navigate = useNavigate();
  const [bookingCount, setBookingCount] = useState(0);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;

    const TIMEOUT_MS = 8000;
    const withTimeout = (p) =>
      Promise.race([
        p,
        new Promise((_, rej) =>
          setTimeout(() => rej(new Error('Request timed out')), TIMEOUT_MS)
        ),
      ]);

    const fetchData = async () => {
      const [bookingsRes, businessRes] = await Promise.allSettled([
        withTimeout(
          axios.get(`${API}/bookings`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        ),
        withTimeout(axios.get(`${API}/business-info`)),
      ]);

      if (bookingsRes.status === 'fulfilled') {
        const pending = (bookingsRes.value.data || []).filter(
          (b) => b.status === 'pending'
        ).length;
        setBookingCount(pending);
      } else {
        console.error('Bookings fetch failed:', bookingsRes.reason?.message);
      }

      if (businessRes.status === 'fulfilled') {
        setBusinessInfo(businessRes.value.data);
      } else {
        console.error('Business info fetch failed:', businessRes.reason?.message);
      }

      setLoading(false);
    };

    fetchData();
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const menuItems = [
    {
      title: 'Bookings',
      description: bookingCount > 0 ? `${bookingCount} pending request${bookingCount !== 1 ? 's' : ''}` : 'View appointment requests',
      icon: Calendar,
      path: '/admin/bookings',
      color: bookingCount > 0 ? 'bg-red-500' : 'bg-amber-500',
      badge: bookingCount > 0 ? bookingCount : null
    },
    {
      title: 'Business Info',
      description: 'Update address, phone, email, hours',
      icon: Building2,
      path: '/admin/business-info',
      color: 'bg-blue-500'
    },
    {
      title: 'Services',
      description: 'Manage your braid styles and services',
      icon: Image,
      path: '/admin/services',
      color: 'bg-purple-500'
    },
    {
      title: 'Pricing',
      description: 'Update service pricing',
      icon: DollarSign,
      path: '/admin/pricing',
      color: 'bg-green-500'
    },
    {
      title: 'Gallery',
      description: 'Manage gallery images',
      icon: FileImage,
      path: '/admin/gallery',
      color: 'bg-pink-500'
    },
    {
      title: 'Testimonials',
      description: 'Manage client testimonials',
      icon: Star,
      path: '/admin/testimonials',
      color: 'bg-yellow-500'
    },
    {
      title: 'Account Settings',
      description: 'Change username and password',
      icon: Settings,
      path: '/admin/settings',
      color: 'bg-gray-500'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-sm text-gray-600 mt-1">Welcome back, {admin?.username}!</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-red-500 text-red-500 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <Phone className="w-6 h-6 text-amber-800" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Contact</p>
                  <p className="text-lg font-semibold">
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      businessInfo?.phone || '—'
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="text-lg font-semibold truncate">
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      businessInfo?.email || '—'
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Location</p>
                  <p className="text-lg font-semibold truncate">
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : businessInfo ? (
                      `${businessInfo.address || ''}${businessInfo.address && businessInfo.city ? ', ' : ''}${businessInfo.city || ''}` || '—'
                    ) : (
                      '—'
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Management Sections */}
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-6">Manage Your Website</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <Card
                  key={item.path}
                  className="hover:shadow-lg transition-shadow duration-200 cursor-pointer relative"
                  onClick={() => navigate(item.path)}
                >
                  {item.badge && (
                    <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                      {item.badge}
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex items-start space-x-4">
                      <div className={`w-12 h-12 ${item.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{item.title}</CardTitle>
                        <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Button className="w-full bg-gray-900 hover:bg-gray-800">
                      Manage {item.title}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Instructions */}
        <Card className="mt-8 bg-amber-50 border-amber-200">
          <CardHeader>
            <CardTitle className="text-amber-900">💡 Quick Guide</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-gray-700">
              <li>• <strong>Business Info:</strong> Update your contact details, address, and operating hours</li>
              <li>• <strong>Services:</strong> Add, edit, or remove braid styles with photos</li>
              <li>• <strong>Pricing:</strong> Keep your service prices up to date</li>
              <li>• <strong>Gallery:</strong> Upload your best work to showcase to clients</li>
              <li>• <strong>Testimonials:</strong> Add client reviews to build trust</li>
            </ul>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminDashboard;
